import { useEffect, useState, useRef } from "react";
import {
  Grid,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Loading from "../../Components/Loading";
import axios from "axios";
import { API_URL } from "../../Config";
import { getAuthToken } from "../../Redux/Service/FetchBaseQuery";
import InfiniteAutocomplete from "../../Components/InfiniteAutocomplete";
import StyledDashboardLayout from "../style";
import UploadLogo from "../../Assets/upload.svg";
import { useGetCurrenciesQuery } from "../../Redux/Service/CurrencyApi";

function EditMilestone() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [milestoneData, setMilestoneData] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const {
    data: currencies,
    isLoading: isLoadingForCurrency,
    error: errorForCurrency,
  } = useGetCurrenciesQuery();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [uploadFile, setUploadFile] = useState("Upload Estimate File");
  const debounceTimeout = useRef(null);
  const [symbol, setSymbol] = useState("$");

  const currencySymbolSet = (currencyId, setFieldValue) => {
    setFieldValue("currencyId", currencyId);
    const selectedCurrency = currencies?.response?.find(
      (val) => val.id === currencyId
    );
    setSymbol(selectedCurrency?.symbol ?? "$");
  };

  // Function to get initial values from milestone data
  const getInitialValues = () => {
    if (!milestoneData) {
      return {
        opportunityId: "",
        quotationId: "",
        estimateId: "",
        proposalId: "",
        name: "",
        type: "",
        delivery: "",
        cost: "",
        currencyId: "",
        file: "",
      };
    }

    return {
      opportunityId: milestoneData.opportunity?.id?.toString() || "",
      quotationId: milestoneData.quotation?.id?.toString() || "",
      estimateId: milestoneData.estimate?.id?.toString() || "",
      proposalId: milestoneData.proposal?.id?.toString() || "",
      name: milestoneData.name || "",
      type: milestoneData.type || "",
      delivery: milestoneData.delivery?.toString() || "",
      cost: milestoneData.cost?.toString() || "",
      currencyId: milestoneData.currency?.id?.toString() || "",
      file: "",
    };
  };

  // Fetch milestone data
  useEffect(() => {
    const fetchMilestoneData = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        const response = await axios.get(
          `${API_URL}milestone/view/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.response) {
          const milestone = response.data.response;
          console.log("Fetched milestone data:", milestone);
          setMilestoneData(milestone);
          
          // Set currency symbol if currency data is available
          if (milestone.currency && currencies?.response) {
            const milestoneCurrency = currencies.response.find(
              (currency) => currency.id === milestone.currency.id
            );
            if (milestoneCurrency) {
              setSymbol(milestoneCurrency.symbol);
            }
          }
        } else {
          toast.error("Failed to fetch milestone data");
          navigate("/milestones");
        }
      } catch (error) {
        console.error("Error fetching milestone:", error);
        toast.error("Failed to fetch milestone data");
        navigate("/milestones");
      } finally {
        setLoading(false);
      }
    };

    if (id && currencies?.response) { // Wait for currencies to load before fetching milestone
      fetchMilestoneData();
    }
  }, [id, navigate, currencies?.response]);

  // Set initial currency symbol when currencies load
  useEffect(() => {
    if (milestoneData && milestoneData.currency && currencies?.response) {
      const milestoneCurrency = currencies.response.find(
        (currency) => currency.id === milestoneData.currency.id
      );
      if (milestoneCurrency) {
        setSymbol(milestoneCurrency.symbol);
      }
    }
  }, [milestoneData, currencies?.response]);

  // Set selected option when milestone data is available
  useEffect(() => {
    if (milestoneData && milestoneData.opportunity && visibleOptions.length === 0) {
      const opportunity = {
        id: milestoneData.opportunity.id,
        name: milestoneData.opportunity.name || milestoneData.opportunity.opportunityId || 'Unknown Opportunity',
        company: milestoneData.opportunity.company || null,
        status: milestoneData.opportunity.status || 'Unknown'
      };
      
      setSelectedOption(opportunity);
      setVisibleOptions([opportunity]);
    }
  }, [milestoneData, visibleOptions.length]);

  // Fetch opportunities
  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(
          `${API_URL}opportunity?limit=10&name=${search}&page=${page}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const newData = response.data?.response?.data || [];
        setOpportunities(response.data?.response || []);

        setVisibleOptions((prevOptions) => {
          const existingIds = new Set(prevOptions.map((opt) => opt.id));
          const uniqueNewData = newData.filter(
            (opt) => !existingIds.has(opt.id)
          );
          return [...prevOptions, ...uniqueNewData];
        });
      } catch (error) {
        console.error("Error fetching opportunities:", error);
        toast.error("Failed to fetch opportunities");
      }
    };
    fetchOpportunities();
  }, [search, page]);

  // Fetch quotations
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}quotation/no-contract`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setQuotations(response.data.response || []);
      } catch (error) {
        console.error("Error fetching quotations:", error);
        toast.error("Failed to fetch quotations");
      }
    };
    fetchQuotations();
  }, []);

  // Fetch estimates
  useEffect(() => {
    const fetchEstimates = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}estimate`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setEstimates(response.data.response || []);
      } catch (error) {
        console.error("Error fetching estimates:", error);
        toast.error("Failed to fetch estimates");
      }
    };
    fetchEstimates();
  }, []);

  // Fetch proposals
  useEffect(() => {
    const fetchProposals = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}proposal`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProposals(response.data.response.data || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching proposals:", error);
        toast.error("Failed to fetch proposals");
        setLoading(false);
      }
    };
    fetchProposals();
  }, []);

  const handleQuotationChange = (event, setFieldValue) => {
    const quotationId = event.target.value;
    setFieldValue("quotationId", quotationId);
  };

  const handleEstimateChange = (event, setFieldValue) => {
    const estimateId = event.target.value;
    setFieldValue("estimateId", estimateId);
  };

  const handleProposalChange = (event, setFieldValue) => {
    const proposalId = event.target.value;
    setFieldValue("proposalId", proposalId);
  };

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    const bottom =
      listboxNode.scrollHeight - listboxNode.scrollTop <=
      listboxNode.clientHeight + 5;

    if (bottom) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSelectOption = (
    event,
    value,
    reason,
    setFieldValue,
    setFieldTouched
  ) => {
    setSelectedOption(value);
    setFieldValue("opportunityId", reason === "selectOption" ? value.id : null);
    if (reason === "clear") {
      setFieldTouched("opportunityId", true);
    }
  };

  const handleSearch = (event, value) => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setSearch(value);
    }, 300);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const token = getAuthToken();

      const formData = new FormData();

      formData.append("opportunityId", values.opportunityId);
      formData.append("name", values.name);
      formData.append("type", values.type);
      formData.append("delivery", values.delivery);
      formData.append("cost", values.cost);
      formData.append("currency", parseInt(values.currencyId));
      if (values.quotationId)
        formData.append("quotationId", values.quotationId);
      if (values.estimateId) formData.append("estimateId", values.estimateId);
      if (values.proposalId) formData.append("proposalId", values.proposalId);
      if (values.file) formData.append("file", values.file);

      await axios.put(`${API_URL}milestone/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Milestone updated successfully!");
      navigate("/milestones");
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
      console.error("Update failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const onFileChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    // check file type
    if (
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      file.type !== "application/vnd.ms-excel"
    ) {
      toast.error("Please select valid file type");
      return;
    }
    setFieldValue("file", file);
    setUploadFile(`File :${file.name}`);
  };

  const validationSchema = Yup.object().shape({
    opportunityId: Yup.string().required("Opportunity is required"),
    name: Yup.string().required("Milestone name is required"),
    type: Yup.string().required("Timeframe is required"),
    delivery: Yup.number().required("Delivery window is required").min(1, "Must be at least 1"),
    cost: Yup.number().required("Cost is required").min(0, "Cost must be positive"),
    currencyId: Yup.string().required("Currency is required"),
  });

  // Show loading spinner while fetching data
  if (loading || isLoadingForCurrency) {
    return (
      <StyledDashboardLayout>
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between mb-2">
                <div className="pageTitle">
                  <h1 className="m-0">Edit Milestone</h1>
                </div>
                <div className="btnWrap">
                  <Link to="/milestones" className="btn addBtn">
                    <ArrowBackIcon /> Back
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="card customCard p-3 m-3">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading milestone data...
              </Typography>
            </Box>
          </div>
        </div>
      </StyledDashboardLayout>
    );
  }

  return (
    <StyledDashboardLayout>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="d-flex justify-content-between mb-2">
              <div className="pageTitle">
                <h1 className="m-0">Edit Milestone</h1>
              </div>
              <div className="btnWrap">
                <Link to="/milestones" className="btn addBtn">
                  <ArrowBackIcon /> Back
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="card customCard p-3 m-3">
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#333",
                mb: 3,
                display: "flex",
                alignItems: "center",
              }}
            >
              Update Milestone Information
            </Typography>
            
            <Divider sx={{ mb: 3 }} />

            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                setFieldValue,
                touched,
                errors,
                isSubmitting,
                handleBlur,
                setFieldTouched,
              }) => (
                <Form>
                  <Box sx={{ padding: "0" }}>
                    <Grid container spacing={3}>
                      {/* Basic Information Section */}
                      <Grid item xs={12}>
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 3, mb: 3 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
                            Basic Information
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Field
                                name="name"
                                as={TextField}
                                fullWidth
                                label="Milestone Name *"
                                variant="outlined"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                      color: "#3F30FF",
                                    },
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl 
                                fullWidth 
                                error={touched.type && Boolean(errors.type)}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                      color: "#3F30FF",
                                    },
                                  },
                                }}
                              >
                                <InputLabel>Timeframe *</InputLabel>
                                <Field
                                  name="type"
                                  as={Select}
                                  label="Timeframe *"
                                  value={values.type || ""}
                                >
                                  <MenuItem value="">Select Timeframe</MenuItem>
                                  <MenuItem value="Days">Days</MenuItem>
                                  <MenuItem value="Weeks">Weeks</MenuItem>
                                  <MenuItem value="Hours">Hours</MenuItem>
                                </Field>
                                <ErrorMessage
                                  name="type"
                                  component="div"
                                  style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", marginLeft: "14px" }}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Field
                                name="delivery"
                                as={TextField}
                                fullWidth
                                type="number"
                                label="Delivery Window *"
                                variant="outlined"
                                inputProps={{ min: 1 }}
                                error={touched.delivery && Boolean(errors.delivery)}
                                helperText={touched.delivery && errors.delivery}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                      color: "#3F30FF",
                                    },
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Field
                                name="cost"
                                as={TextField}
                                fullWidth
                                type="number"
                                label="Cost *"
                                variant="outlined"
                                inputProps={{ min: 0, step: "0.01" }}
                                error={touched.cost && Boolean(errors.cost)}
                                helperText={touched.cost && errors.cost}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                      color: "#3F30FF",
                                    },
                                  },
                                }}
                              />
                            </Grid>

                            {/* Currency Field - Enhanced */}
                            <Grid item xs={12} md={6}>
                              <FormControl 
                                fullWidth 
                                error={touched.currencyId && Boolean(errors.currencyId)}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#3F30FF",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                      color: "#3F30FF",
                                    },
                                  },
                                }}
                              >
                                <InputLabel>Currency *</InputLabel>
                                <Field
                                  name="currencyId"
                                  as={Select}
                                  label="Currency *"
                                  value={values.currencyId || ""}
                                  onChange={(event) => {
                                    const selectedCurrencyId = event.target.value;
                                    setFieldValue("currencyId", selectedCurrencyId);
                                    currencySymbolSet(selectedCurrencyId, setFieldValue);
                                  }}
                                >
                                  <MenuItem value="">Select Currency</MenuItem>
                                  {currencies?.response?.map((val, index) => (
                                    <MenuItem key={index} value={val.id.toString()}>
                                      {val.name} ({val.code}) - {val.symbol}
                                    </MenuItem>
                                  ))}
                                </Field>
                                <ErrorMessage
                                  name="currencyId"
                                  component="div"
                                  style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", marginLeft: "14px" }}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Opportunity Selection */}
                      <Grid item xs={12}>
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 3, mb: 3 }}>
                          <Typography 
                            variant="h6" 
                            gutterBottom 
                            sx={{ 
                              fontWeight: 600, 
                              color: "#333", 
                              mb: 2,
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            Opportunity Selection
                            <Chip
                              label="Required"
                              color="error"
                              size="small"
                              sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                            />
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <InfiniteAutocomplete
                                onBlur={handleBlur}
                                value={selectedOption}
                                onChange={(event, value, reason) =>
                                  handleSelectOption(
                                    event,
                                    value,
                                    reason,
                                    setFieldValue,
                                    setFieldTouched
                                  )
                                }
                                options={visibleOptions}
                                onScroll={handleScroll}
                                inputValue={search}
                                setInputValue={handleSearch}
                                total_options={"Opportunity"}
                                error={Boolean(touched.opportunityId && errors.opportunityId)}
                                helperText={touched.opportunityId && errors.opportunityId}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id} style={{ padding: '8px 16px' }}>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      width: '100%',
                                      overflow: 'hidden'
                                    }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight="medium"
                                        sx={{
                                          textOverflow: 'ellipsis',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {option.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          textOverflow: 'ellipsis',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {option.company?.name || 'No company'} • {option.status || 'No status'}
                                      </Typography>
                                    </div>
                                  </li>
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Optional Documents */}
                      <Grid item xs={12}>
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 3, mb: 3 }}>
                          <Typography 
                            variant="h6" 
                            gutterBottom 
                            sx={{ 
                              fontWeight: 600, 
                              color: "#333", 
                              mb: 2,
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            Link Documents
                            <Chip
                              label="Optional"
                              color="primary"
                              size="small"
                              sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                            />
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Select Quotation</InputLabel>
                                <Field
                                  name="quotationId"
                                  as={Select}
                                  label="Select Quotation"
                                  value={values.quotationId || ""}
                                  onChange={(event) =>
                                    handleQuotationChange(event, setFieldValue)
                                  }
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {quotations.map((quotation) => (
                                    <MenuItem key={quotation.id} value={quotation.id}>
                                      {quotation.title}
                                    </MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Select Estimate</InputLabel>
                                <Field
                                  name="estimateId"
                                  as={Select}
                                  label="Select Estimate"
                                  value={values.estimateId || ""}
                                  onChange={(event) =>
                                    handleEstimateChange(event, setFieldValue)
                                  }
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {estimates.map((estimate) => (
                                    <MenuItem key={estimate.id} value={estimate.id}>
                                      {estimate.name}
                                    </MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Select Proposal</InputLabel>
                                <Field
                                  name="proposalId"
                                  as={Select}
                                  label="Select Proposal"
                                  value={values.proposalId || ""}
                                  onChange={(event) =>
                                    handleProposalChange(event, setFieldValue)
                                  }
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {proposals.map((proposal) => (
                                    <MenuItem key={proposal.id} value={proposal.id}>
                                      {proposal.title}
                                    </MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* File Upload */}
                      <Grid item xs={12}>
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 3, mb: 3 }}>
                          <Typography 
                            variant="h6" 
                            gutterBottom 
                            sx={{ 
                              fontWeight: 600, 
                              color: "#333", 
                              mb: 2,
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            Upload File
                            <Chip
                              label="Optional"
                              color="primary"
                              size="small"
                              sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                            />
                          </Typography>
                          
                          <Paper
                            elevation={1}
                            sx={{
                              p: 3,
                              textAlign: "center",
                              cursor: "pointer",
                              border: "2px dashed #e0e0e0",
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              "&:hover": {
                                borderColor: "#3F30FF",
                                backgroundColor: "#f8f9ff",
                              },
                            }}
                            onClick={() => document.getElementById("file").click()}
                          >
                            <img
                              src={UploadLogo}
                              alt="Upload"
                              style={{ width: "48px", height: "48px", marginBottom: "16px" }}
                            />
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              Drag and drop or{" "}
                              <Typography
                                component="span"
                                sx={{ color: "#3F30FF", fontWeight: 600, cursor: "pointer" }}
                              >
                                choose file
                              </Typography>{" "}
                              to upload
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Excel files only (.xlsx, .xls)
                            </Typography>
                            {uploadFile !== "Upload Estimate File" && (
                              <Typography variant="body2" sx={{ mt: 1, color: "#4CAF50", fontWeight: 500 }}>
                                {uploadFile}
                              </Typography>
                            )}
                          </Paper>

                          <input
                            id="file"
                            type="file"
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            style={{ display: "none" }}
                            onChange={(event) => onFileChange(event, setFieldValue)}
                          />
                          <ErrorMessage
                            name="file"
                            component="div"
                            style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "8px" }}
                          />
                        </Box>
                      </Grid>

                      {/* Submit Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate("/milestones")}
                            style={{
                              padding: "10px 24px",
                              borderRadius: "6px",
                              border: "1px solid #6c757d",
                              color: "#6c757d",
                              backgroundColor: "transparent",
                              fontWeight: 500,
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                              padding: "10px 24px",
                              borderRadius: "6px",
                              border: "none",
                              backgroundColor: "#3F30FF",
                              color: "white",
                              fontWeight: 500,
                              minWidth: "120px",
                              cursor: isSubmitting ? "not-allowed" : "pointer",
                              opacity: isSubmitting ? 0.7 : 1,
                            }}
                          >
                            {isSubmitting ? "Updating..." : "Update Milestone"}
                          </button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </div>
      </div>
    </StyledDashboardLayout>
  );
}

export default EditMilestone;
